import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Bell, CheckCircle, Warning, PaperPlaneTilt } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { fmtYmdSeconds } from "@/lib/dateFormat";

export default function Notifications() {
    const { user } = useAuth();
    const [list, setList] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        title: "", body: "", channels: ["inapp", "email"], priority: "normal",
    });

    const load = async () => {
        const { data } = await api.get("/notifications");
        setList(data);
    };
    useEffect(() => { load(); }, []);

    const markRead = async (id) => {
        await api.post(`/notifications/${id}/read`);
        await load();
    };

    const markAllRead = async () => {
        await api.post(`/notifications/read-all`);
        await load();
    };

    const toggleChannel = (ch) => {
        setForm((f) => ({
            ...f,
            channels: f.channels.includes(ch) ? f.channels.filter((c) => c !== ch) : [...f.channels, ch],
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        try {
            const { data } = await api.post("/notifications/send", { ...form, user_ids: [] });
            toast.success(`${data.sent} 件送信しました`);
            setOpen(false);
            setForm({ title: "", body: "", channels: ["inapp", "email"], priority: "normal" });
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const canSend = ["super_admin", "company_admin"].includes(user?.role);

    return (
        <div className="space-y-5" data-testid="notifications-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="prc-label-overline">通知センター</p>
                    <h1 className="text-[1.685rem] font-bold tracking-tight">受信通知</h1>
                </div>
                <div className="flex gap-2">
                    <button onClick={markAllRead} className="prc-btn-ghost text-sm" data-testid="notif-read-all-btn">全て既読にする</button>
                    {canSend && (
                        <button onClick={() => setOpen(true)} className="prc-btn-primary inline-flex items-center gap-2" data-testid="notif-new-btn">
                            <PaperPlaneTilt size={16} />通知を配信
                        </button>
                    )}
                </div>
            </div>

            <div className="prc-card divide-y divide-[var(--prc-border)]">
                {list.length === 0 ? (
                    <p className="p-6 text-sm text-[var(--prc-muted)] text-center" data-testid="notif-empty">新しい通知はありません</p>
                ) : list.map((n) => (
                    <div key={n.id} className={`p-4 ${!n.read ? "bg-[var(--prc-bg)]" : ""}`} data-testid={`notif-${n.id}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 flex-1">
                                {n.priority === "critical" || n.priority === "high"
                                    ? <Warning size={24} className="text-[var(--prc-red)] mt-0.5" weight="fill" />
                                    : <Bell size={24} className="text-[var(--prc-accent)] mt-0.5" />}
                                <div className="flex-1">
                                    <p className="font-semibold text-sm">{n.title} {!n.read && <span className="prc-badge info ml-2">新着</span>}</p>
                                    <p className="text-sm text-[var(--prc-muted)] whitespace-pre-wrap mt-1">{n.body}</p>
                                    <p className="text-xs text-[var(--prc-tertiary)] mt-2">
                                        {fmtYmdSeconds(n.created_at, String(n.created_at).substring(0, 19).replace("T", " "))} · {n.channel} · {n.priority}
                                    </p>
                                </div>
                            </div>
                            {!n.read && (
                                <button onClick={() => markRead(n.id)} className="text-xs font-semibold text-[var(--prc-muted)] hover:text-[var(--prc-ink)]" data-testid={`notif-read-${n.id}`}>既読</button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {open && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="notif-dialog">
                    <form onSubmit={submit} className="prc-card p-6 max-w-lg w-full">
                        <p className="prc-label-overline mb-1">社内通知を配信</p>
                        <h2 className="text-xl font-bold mb-4">全社員に送信</h2>
                        <div className="space-y-3">
                            <div>
                                <label className="prc-label-overline block mb-1">件名 *</label>
                                <input required className="prc-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="notif-title" />
                            </div>
                            <div>
                                <label className="prc-label-overline block mb-1">本文 *</label>
                                <textarea required className="prc-input min-h-24" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="notif-body" />
                            </div>
                            <div>
                                <label className="prc-label-overline block mb-1">配信チャネル</label>
                                <div className="flex gap-2 flex-wrap">
                                    {[
                                        { v: "inapp", l: "アプリ内" },
                                        { v: "email", l: "社内メール" },
                                        { v: "line", l: "LINE (モック)" },
                                        { v: "sms", l: "SMS (モック)" },
                                    ].map((c) => (
                                        <button
                                            key={c.v}
                                            type="button"
                                            onClick={() => toggleChannel(c.v)}
                                            className={`px-3 py-1.5 text-xs font-semibold border rounded-sm ${form.channels.includes(c.v) ? "bg-[var(--prc-green)] text-black border-[var(--prc-green)]" : "bg-[var(--prc-surface-2)] border-[var(--prc-border-strong)] text-[var(--prc-muted)]"}`}
                                            data-testid={`notif-ch-${c.v}`}
                                        >
                                            {c.l}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="prc-label-overline block mb-1">優先度</label>
                                <select className="prc-input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="notif-priority">
                                    <option value="low">低</option>
                                    <option value="normal">通常</option>
                                    <option value="high">高</option>
                                    <option value="critical">緊急</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button type="button" onClick={() => setOpen(false)} className="prc-btn-ghost" data-testid="notif-cancel">キャンセル</button>
                            <button type="submit" className="prc-btn-primary" data-testid="notif-send">配信</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
